import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// Internal imports
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { checkUsername as checkUsernameAction, registerCreator } from '../../../../shared/actions/authActions';
import { showError } from '../../../../shared/utils/swalHandler';
import { getCreatorRegistrationErrorMessage } from '../getCreatorRegistrationErrorMessage';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormControlWithFocus,
  FormMessageWithValidation,
  FormDescription,
  useFormValidation,
  handleInputChange,
} from "../../../../shared/components/form";
import UserUploadFile from "../../../../features/cards/UserUploadFile";
import PhoneInputWithCountry from "../../../../shared/components/PhoneInputWithCountry";
import HeaderSection from '../../../../components/layout/header';
import { MAX_FILE_SIZE } from '../../../../shared/constants/valisdation';
import {
  VALIDATION_STATUS,
  ACCOUNT_TYPES,
  DOCUMENT_TYPES,
  FILE_TYPES,
  getSubscriptionType,
  getImageData
} from '../constant';
import {
  sanitizeNameInput,
  validateUsername,
  sanitizeUsernameInput,
  validateEmail,
  getMaxDateOfBirth,
  getMinDateOfBirth,
  removePhoneNumberFormatting
} from '../../../../shared/utils/function';
import { validateFileSize } from '../../../../shared/utils/fileValidation';
import { createFileData } from '../../../../shared/utils/fileOperations';


/** Escape HTML for safe use in registration error list */
const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Build ul/li HTML from an array of error messages for the Registration Failed modal */
const formatRegistrationErrorList = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return '';
  const items = messages.map((m) => `<li>${escapeHtml(m)}</li>`).join('');
  return `<ul class="registration-error-list">${items}</ul>`;
};

/**
 * CreatorRegistration component for handling creator account registration
 * @returns {JSX.Element} The registration form component
 */
const CreatorRegistration = ({
  form,
  onBack,
  referralId = '',
  showWelcomeTagsSummary = true,
}) => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const [previousDocumentType, setPreviousDocumentType] = useState(() => form.getValues('documentType') || DOCUMENT_TYPES.ID);
  const usernameDebounceRef = useRef(null);

  // Add the validation hook - this is the main change needed
  const {
    validateAndFocus,
    shouldShowError,
  } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  // State management
  const [uploadedFiles, setUploadedFiles] = useState({
    [FILE_TYPES.PROFILE_IMAGE]: null,
    [FILE_TYPES.ID_FRONT]: null,
    [FILE_TYPES.ID_BACK]: null
  });

  const [uiState, setUiState] = useState({
    showPassword: false,
    showConfirmPassword: false,
    isLoading: false,
    isUsernameAvailable: null,
    isEmailValid: null
  });

  // Watch only required fields to avoid unnecessary re-renders on every keystroke
  const accountType = form.watch('accountType');
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');
  const email = form.watch('email');
  const username = form.watch('username');
  const password = form.watch('password');
  const confirmPassword = form.watch('confirmPassword');
  const documentType = form.watch('documentType');
  const selectedTags = form.watch('selectedTags');
  const profileImage = form.watch('profileImage');
  const idFront = form.watch('idFront');
  const idBack = form.watch('idBack');

  const selectedCategories = selectedTags || [];
  const showLegalNameHint = !(firstName?.trim() && lastName?.trim());

  // Set referral ID from props or URL params
  useEffect(() => {
    // Get referral ID from URL params or props
    const urlParams = new URLSearchParams(window.location.search);
    const referralParam = urlParams.get('referral') || referralId;
    if (referralParam) {
      form.setValue('referralId', referralParam);
    }
  }, [form, referralId]);

  // Rehydrate uploadedFiles from form when returning to this step (e.g. back from ProfileTags)
  // so document upload previews and values are restored
  useEffect(() => {
    const profileImageVal = form.getValues(FILE_TYPES.PROFILE_IMAGE);
    const idFrontVal = form.getValues(FILE_TYPES.ID_FRONT);
    const idBackVal = form.getValues(FILE_TYPES.ID_BACK);

    const isFile = (v) => v && (v instanceof File || (typeof v === 'object' && Number(v?.size) > 0));

    const readFileAsData = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(createFileData(file, e.target.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    const run = async () => {
      const next = {};
      if (isFile(profileImageVal)) {
        next[FILE_TYPES.PROFILE_IMAGE] = await readFileAsData(profileImageVal instanceof File ? profileImageVal : profileImageVal);
      }
      if (isFile(idFrontVal)) {
        next[FILE_TYPES.ID_FRONT] = await readFileAsData(idFrontVal instanceof File ? idFrontVal : idFrontVal);
      }
      if (isFile(idBackVal)) {
        next[FILE_TYPES.ID_BACK] = await readFileAsData(idBackVal instanceof File ? idBackVal : idBackVal);
      }
      if (Object.keys(next).length > 0) {
        setUploadedFiles(prev => ({ ...prev, ...next }));
      }
    };
    run();
  }, [form]);

  // Reset idFront and idBack when document type changes (documentType comes from values below)
  useEffect(() => {
    if (documentType !== previousDocumentType) {
      setUploadedFiles(prev => ({
        ...prev,
        [FILE_TYPES.ID_FRONT]: null,
        [FILE_TYPES.ID_BACK]: null
      }));
      form.setValue(FILE_TYPES.ID_FRONT, null);
      form.setValue(FILE_TYPES.ID_BACK, null);
      setPreviousDocumentType(documentType);
    }
  }, [documentType, form, previousDocumentType]);

  // Trigger validation only for file fields when document type or uploaded files change
  useEffect(() => {
    form.trigger([
      FILE_TYPES.PROFILE_IMAGE,
      FILE_TYPES.ID_FRONT,
      FILE_TYPES.ID_BACK
    ]);
  }, [documentType, uploadedFiles, form]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate.toCreatorSignup();
    }
  }, [navigate, onBack]);

  const checkUsername = useCallback(async (username) => {
    if (username.length < 3 || !validateUsername(username)) {
      setUiState(prev => ({ ...prev, isUsernameAvailable: false }));
      return;
    }

    try {
      const result = await dispatch(checkUsernameAction(username));
      const isAvailable = result.success && result.data.isValid;
      setUiState(prev => ({ ...prev, isUsernameAvailable: isAvailable }));
    } catch (error) {
      setUiState(prev => ({ ...prev, isUsernameAvailable: false }));
    }
  }, [dispatch]);

  // When returning to this step (e.g. from Interests), re-run username/email checks
  // so we don't show loading for an already-validated username
  useEffect(() => {
    const username = form.getValues('username');
    if (username && username.length >= 3 && validateUsername(username)) {
      checkUsername(username);
    }
    const email = form.getValues('email');
    if (email) {
      setUiState(prev => ({ ...prev, isEmailValid: validateEmail(email) }));
    }
  }, [form, checkUsername]);

  const handleFileUpload = useCallback(async (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      const isValid = await validateFileSize(file, MAX_FILE_SIZE.CREATOR_REGISTRATION);

      if (!isValid) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        const fileData = createFileData(file, event.target.result);
        setUploadedFiles(prev => ({
          ...prev,
          [fileType]: fileData
        }));
        form.setValue(fileType, file, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        await form.trigger(fileType);
      };
      reader.readAsDataURL(file);
    }
  }, [form]);

  const handleRemoveFile = useCallback(async (fileType) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: null
    }));
    form.setValue(fileType, null, {
      shouldValidate: true,
      shouldDirty: true
    });
    await form.trigger(fileType);
  }, [form]);

  const onSubmit = useCallback(async (data) => {
    const isValid = await validateAndFocus();
    if (!isValid) return;

    if (!uiState.isUsernameAvailable) {
      toast.error('Please choose a valid username');
      setUiState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    if (!uiState.isEmailValid) {
      toast.error('Please enter a valid email address');
      setUiState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // Prepare data for API call
    const imageData = getImageData(data.documentType, uploadedFiles);
    const registrationData = {
      email: data.email,
      password: data.password,
      first_name: data.firstName,
      middle_name: data.middleName || "",
      last_name: data.lastName,
      phone_country_code: data.phoneCountry || "+1",
      phone_number: parseInt(removePhoneNumberFormatting(data.phoneNumber)) || 0,
      username: data.username,
      image_profile: imageData.profileImage,
      upload_type: data.documentType,
      image_id_front: imageData?.idFront || "",
      image_id_back: imageData?.idBack || "",
      dob: data.dateOfBirth || "",
      categories: data.selectedTags,
      ...(data.referralId && data.referralId !== "" && data.referralId !== null && data.referralId !== undefined && {
        referral_id: data.referralId
      }),
      profile_description: "",
      address: "",
      country: "",
      state: "",
      // city: "",
      // postal: "",
      // wishlistUrl: "",
      // pinMessage: "",
    };

    setUiState(prev => ({ ...prev, isLoading: true }));
    try {
      const subType = getSubscriptionType(data.accountType);
      const result = await dispatch(registerCreator(registrationData, subType));

      if (result.success) {
        navigate.toUnderReview();
      } else {
        const messages = getCreatorRegistrationErrorMessage(
          result.error || "Something went wrong. Please try again.",
          result.fields
        );
        const registrationErrorHtml = formatRegistrationErrorList(messages);
        await showError("Registration Failed", registrationErrorHtml, {
          customClass: {
            popup: 'registration-failed-modal',
            htmlContainer: 'registration-error-container'
          }
        });
      }
    } catch (error) {
      const fields = error.error?.details ?? error.fields ?? error.response?.data?.fields;
      const rawMessage = error.response?.data?.mongoCode === 11000
        ? "Account with that email already exists"
        : error.response?.data?.message || error.message || "Something went wrong. Please try again.";
      const messages = getCreatorRegistrationErrorMessage(rawMessage, fields);
      const registrationErrorHtml = formatRegistrationErrorList(messages);
      await showError("Registration Failed", registrationErrorHtml, {
        customClass: {
          popup: 'registration-failed-modal',
          htmlContainer: 'registration-error-container'
        }
      });
    } finally {
      setUiState(prev => ({ ...prev, isLoading: false }));
    }
  }, [validateAndFocus, uiState.isUsernameAvailable, uiState.isEmailValid, uploadedFiles, navigate, dispatch, form]);

  // Validation status (values, not getters)
  const usernameStatus = useMemo(() => {
    if (!username) return VALIDATION_STATUS.EMPTY;
    if (username.length < 3) return VALIDATION_STATUS.INVALID;
    if (uiState.isUsernameAvailable === null) return VALIDATION_STATUS.CHECKING;
    return uiState.isUsernameAvailable ? VALIDATION_STATUS.VALID : VALIDATION_STATUS.INVALID;
  }, [username, uiState.isUsernameAvailable]);

  const getPasswordStatus = useMemo(() => {
    const passwordErrors = form.formState.errors.password;
    if (!password) return VALIDATION_STATUS.EMPTY;
    return passwordErrors ? VALIDATION_STATUS.INVALID : VALIDATION_STATUS.VALID;
  }, [password, form.formState.errors.password]);

  const getConfirmPasswordStatus = useMemo(() => {
    if (!confirmPassword) return VALIDATION_STATUS.EMPTY;
    return password === confirmPassword ? VALIDATION_STATUS.VALID : VALIDATION_STATUS.INVALID;
  }, [password, confirmPassword]);

  const usernameClass = {
    [VALIDATION_STATUS.VALID]: "valid",
    [VALIDATION_STATUS.INVALID]: "invalid",
    [VALIDATION_STATUS.CHECKING]: "checking",
  }[usernameStatus] || "";

  const passwordClass = {
    [VALIDATION_STATUS.VALID]: "valid",
    [VALIDATION_STATUS.INVALID]: "invalid",
  }[getPasswordStatus] || "";

  const handleEmailChange = useCallback((field, value) => {
    field.onChange(value);
    setUiState(prev => ({
      ...prev,
      isEmailValid: value.length > 3 ? validateEmail(value) : null
    }));
  }, []);

  const handleUsernameChange = useCallback((field, value) => {
    const sanitized = sanitizeUsernameInput(value);
    field.onChange(sanitized);

    if (sanitized.length < 3) {
      setUiState(prev => ({ ...prev, isUsernameAvailable: null }));
    }

    if (usernameDebounceRef.current) {
      clearTimeout(usernameDebounceRef.current);
    }

    usernameDebounceRef.current = setTimeout(async () => {
      if (sanitized.length >= 3) {
        const isValid = await form.trigger('username');
        if (isValid) {
          checkUsername(sanitized);
        }
      }
    }, 400);
  }, [checkUsername, form]);

  useEffect(() => {
    return () => {
      if (usernameDebounceRef.current) {
        clearTimeout(usernameDebounceRef.current);
      }
    };
  }, []);

  const handleTermsClick = useCallback(() => {
    navigate.toTerms();
  }, [navigate]);

  const handlePrivacyClick = useCallback(() => {
    navigate.toPrivacy();
  }, [navigate]);

  const handleLoginLink = useCallback(() => {
    navigate.toLogin();
  }, [navigate]);

  // Create button: schema validity + async checks + file uploads (single source of truth via form.formState.isValid)
  const hasProfileFile = !!(uploadedFiles[FILE_TYPES.PROFILE_IMAGE] || (profileImage && (profileImage instanceof File || (typeof profileImage === 'object' && Number(profileImage?.size) > 0))));
  const hasIdFrontFile = !!(uploadedFiles[FILE_TYPES.ID_FRONT] || (idFront && (idFront instanceof File || (typeof idFront === 'object' && Number(idFront?.size) > 0))));
  const hasIdBackFile = !!(uploadedFiles[FILE_TYPES.ID_BACK] || (idBack && (idBack instanceof File || (typeof idBack === 'object' && Number(idBack?.size) > 0))));
  const idBackOk = documentType === DOCUMENT_TYPES.PASSPORT ? true : hasIdBackFile;

  const isFormValid = form.formState.isValid;
  const hasTags = (selectedTags?.length ?? 0) > 0;
  const isDocTypeValid =
    documentType === DOCUMENT_TYPES.ID ||
    documentType === DOCUMENT_TYPES.PASSPORT;

  const isCreateEnabled =
    !uiState.isLoading &&
    !form.formState.isSubmitting &&
    isFormValid &&
    uiState.isUsernameAvailable === true &&
    uiState.isEmailValid === true &&
    hasTags &&
    isDocTypeValid &&
    hasProfileFile &&
    hasIdFrontFile &&
    idBackOk;

  return (
    <div className={`creator-registration-screen`}>
      <ToastContainer />

      {/* Animated Background Elements */}
      <div className="modern-bg-elements">
        <div className="modern-bg-blur-1"></div>
        <div className="modern-bg-blur-2"></div>
        <div className="modern-bg-blur-3"></div>
      </div>

      {/* Header */}
      <HeaderSection
        type="text"
        title="Register"
        handleBack={handleBack}
      />

      {/* Main Content */}
      <main className="modern-main">
        <div className="modern-welcome-section">
          <div className="modern-welcome-icon">
            <i className="fas fa-user-plus"></i>
          </div>
          <h2 className="modern-welcome-title">Create Your Account</h2>
          <p className="modern-welcome-description">
            Enter your name and birthdate exactly as shown on your ID or passport for verification
          </p>
          {showWelcomeTagsSummary && selectedCategories.length > 0 && (
            <div className="modern-tags-preview">
              <p className="modern-tags-text">
                <i className="fas fa-tags"></i>
                {selectedCategories.length} profile tag{selectedCategories.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="modern-form" autoComplete="off">
            {/* Account Type Selection */}
            <FormField
              control={form.control}
              name="accountType"
              render={({ field }) => (
                <FormItem className="modern-form-section">
                  <FormLabel className="modern-form-label">
                    <i className="modern-form-icon fas fa-crown"></i>
                    Account Type
                  </FormLabel>
                  <FormControlWithFocus fieldName="accountType">
                    <div className="modern-account-grid">
                      <div
                        className={`modern-account-option ${field.value === ACCOUNT_TYPES.MONTHLY ? 'selected' : ''}`}
                        onClick={() => field.onChange(ACCOUNT_TYPES.MONTHLY)}
                        tabIndex={0}
                        role="button"
                        aria-label="Select Monthly Subscription"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            field.onChange(ACCOUNT_TYPES.MONTHLY);
                          }
                        }}
                      >
                        <div className="modern-account-content">
                          <div className="modern-account-icon">
                            <i style={{ color: "#a3a3a3", height: "1.1rem" }} className="fas fa-calendar-days"></i>
                          </div>
                          <span className="modern-account-title">Monthly Subscription</span>
                          <div className={`modern-account-radio ${field.value === ACCOUNT_TYPES.MONTHLY ? 'selected' : ''}`}>
                            <div className={`modern-radio-dot ${field.value === ACCOUNT_TYPES.MONTHLY ? 'active' : ''}`}></div>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`modern-account-option ${field.value === ACCOUNT_TYPES.PPV ? 'selected' : ''}`}
                        onClick={() => field.onChange(ACCOUNT_TYPES.PPV)}
                        tabIndex={0}
                        role="button"
                        aria-label="Select Pay Per View"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            field.onChange(ACCOUNT_TYPES.PPV);
                          }
                        }}
                      >
                        <div className="modern-account-content">
                          <div className="modern-account-icon">
                            <i style={{ color: "#a3a3a3", height: "2rem", width: "1.3rem" }} className="fas fa-eye"></i>
                          </div>
                          <span className="modern-account-title">Pay Per View</span>
                          <div className={`modern-account-radio ${field.value === ACCOUNT_TYPES.PPV ? 'selected' : ''}`}>
                            <div className={`modern-radio-dot ${field.value === ACCOUNT_TYPES.PPV ? 'active' : ''}`}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="accountType"
                    useValidation={true}
                  />
                </FormItem>
              )}
            />
            {showLegalNameHint && (
              <p className='text-center'>
                <span className='text-red-1'>Legal name (must match your ID or passport exactly)</span>
              </p>
            )}

            {/* Name Fields */}
            <div className="modern-form-section">
              <div className="modern-name-grid">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="modern-form-field">
                      <FormLabel className="modern-field-label">
                        <i className="modern-field-icon fas fa-user"></i>
                        First Name <span className="modern-field-required">*</span>
                      </FormLabel>
                      <FormControlWithFocus fieldName="firstName">
                        <div className="modern-input-container">
                          <input
                            type="text"
                            className={`modern-input ${shouldShowError('firstName') ? 'error' : ''}`}
                            placeholder="John"
                            autoComplete="off"
                            {...field}
                            onChange={(e) => handleInputChange(field, e.target.value, sanitizeNameInput)}
                          />
                        </div>
                      </FormControlWithFocus>
                      {/* <FormMessageWithValidation
                        fieldName="firstName"
                        useValidation={true}
                      /> */}
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="modern-form-field">
                      <FormLabel className="modern-field-label">
                        <i className="modern-field-icon fas fa-signature"></i>
                        Last Name <span className="modern-field-required">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="modern-input-container">
                          <input
                            type="text"
                            className={`modern-input ${shouldShowError('lastName') ? 'error' : ''}`}
                            placeholder="Doe"
                            autoComplete="off"
                            {...field}
                            onChange={(e) => handleInputChange(field, e.target.value, sanitizeNameInput)}
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Email & Username */}
            <div className="modern-form-section">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="modern-form-field">
                    <FormLabel htmlFor="email" className="modern-field-label">
                      <i className="modern-field-icon fas fa-envelope"></i>
                      Email Address <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="email">
                      <div className="modern-input-container">
                        <input
                          id="email"
                          type="email"
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          className={`modern-input 
                            ${uiState.isEmailValid === false ? 'invalid' : uiState.isEmailValid === true ? 'valid' : ''} 
                            ${shouldShowError('email') ? 'error' : ''}`
                          }
                          placeholder="your@email.com"
                          {...field}
                          onChange={(e) => handleEmailChange(field, e.target.value)}
                        />
                        {uiState.isEmailValid === true && (
                          <div className="modern-validation-icon valid">
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                        {uiState.isEmailValid === false && (
                          <div className="modern-validation-icon invalid">
                            <i className="fas fa-times"></i>
                          </div>
                        )}
                      </div>
                    </FormControlWithFocus>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem className="modern-form-field" style={{ marginTop: "25px" }}>
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-at"></i>
                      Username <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="username">
                      <>
                        <div className="modern-input-container">
                          <input
                            type="text"
                            className={`modern-input ${usernameClass} ${shouldShowError('username') ? 'error' : ''}`}
                            placeholder="@username"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                            maxLength={30}
                            {...field}
                            onChange={(e) => handleUsernameChange(field, e.target.value)}
                            autoFocus={false}
                          />

                          {usernameStatus === VALIDATION_STATUS.VALID && (
                            <div className="modern-validation-icon valid">
                              <i className="fas fa-check"></i>
                            </div>
                          )}
                          {usernameStatus === VALIDATION_STATUS.INVALID && (
                            <div className="modern-validation-icon invalid">
                              <i className="fas fa-times "></i>
                            </div>
                          )}
                          {usernameStatus === VALIDATION_STATUS.CHECKING && (
                            <div className="modern-validation-icon checking">
                              <i className="fas fa-spinner fa-spin"></i>
                            </div>
                          )}
                        </div>
                        <p className="modern-field-hint">
                          <i className="modern-hint-icon fas fa-info-circle"></i>
                          Minimum 3 characters
                        </p>
                      </>
                    </FormControlWithFocus>
                    {field.value && (
                      <div className="modern-field-message">
                        {usernameStatus === VALIDATION_STATUS.INVALID && field.value.length > 2 && (
                          <p className="modern-error-text">
                            <i className="fas fa-exclamation-triangle"></i>
                            This username is taken
                          </p>
                        )}
                        {usernameStatus === VALIDATION_STATUS.VALID && (
                          <p className="modern-success-text">
                            <i className="fas fa-check-circle"></i>
                            Username is available
                          </p>
                        )}
                      </div>
                    )}
                    {username && (
                      <FormMessageWithValidation
                        fieldName="username"
                        useValidation={true}
                        className="fas fa-exclamation-triangle"
                      />
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Password Fields */}
            <div className="modern-form-section">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="modern-form-field">
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-lock"></i>
                      Password <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="password">
                      <div className="modern-input-container">
                        <input
                          type={uiState.showPassword ? "text" : "password"}
                          className={`modern-input ${passwordClass} ${shouldShowError('password') ? 'error' : ''}`}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setUiState(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                          className="modern-password-toggle"
                        >
                          <FontAwesomeIcon icon={uiState.showPassword ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    <FormDescription className="modern-password-hint">
                      <p className="modern-password-requirements">
                        <i className="modern-hint-icon fas fa-shield-halved"></i>
                        Must be at least 6 characters with uppercase, lowercase, number, and special character
                      </p>
                    </FormDescription>
                    {field.value && (
                      <div className="modern-field-message">
                        {getPasswordStatus === VALIDATION_STATUS.VALID && (
                          <p className="modern-success-text">
                            <i className="fas fa-check-circle"></i>
                            Password meets all requirements
                          </p>
                        )}
                      </div>
                    )}
                    {form.watch('password') && (
                      <FormMessageWithValidation
                        fieldName="password"
                        useValidation={true}
                        className="fas fa-exclamation-triangle"
                      />
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="modern-form-field mt-3">
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-lock-open"></i>
                      Confirm Password <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="confirmPassword">
                      <div className="modern-input-container">
                        <input
                          type={uiState.showConfirmPassword ? "text" : "password"}
                          className={`modern-input ${getConfirmPasswordStatus} ${shouldShowError('confirmPassword') ? 'error' : ''}`}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setUiState(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }))}
                          className="modern-password-toggle"
                        >
                          <FontAwesomeIcon icon={uiState.showConfirmPassword ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    {form.watch('confirmPassword') && (
                      <FormMessageWithValidation
                        fieldName="confirmPassword"
                        useValidation={true}
                        className="fas fa-exclamation-triangle"
                      />
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Date of Birth */}
            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field }) => (
                <FormItem className="modern-form-field">
                  <FormLabel className="modern-field-label">
                    <i className="modern-field-icon fas fa-cake-candles"></i>
                    Date of Birth <span className="modern-field-required">*</span>
                  </FormLabel>
                  <FormControlWithFocus fieldName="dateOfBirth">
                    <div className="modern-input-container modern-date-input-container modern-datepicker-wrapper">
                      <DatePicker
                        selected={field.value ? new Date(field.value + 'T12:00:00') : null}
                        onChange={(date) => field.onChange(date ? date.toISOString().slice(0, 10) : '')}
                        onBlur={field.onBlur}
                        minDate={new Date(getMinDateOfBirth() + 'T12:00:00')}
                        maxDate={new Date(getMaxDateOfBirth() + 'T12:00:00')}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Select Date of Birth"
                        onFocus={(e) => e.target.blur()}
                        inputMode="none"
                        onKeyDown={(e) => e.preventDefault()}
                        className={`modern-input modern-datepicker-input ${shouldShowError('dateOfBirth') ? 'error' : ''}`}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                        popperContainer={({ children }) =>
                          ReactDOM.createPortal(children, document.body)
                        }
                      />
                      <i className="modern-date-icon fas fa-calendar" aria-hidden="true"></i>
                    </div>
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="dateOfBirth"
                    useValidation={true}
                    className="fas fa-exclamation-triangle"
                  />
                </FormItem>
              )}
            />

            {/* Phone Number */}
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem className="modern-form-field">
                  <FormLabel className="modern-field-label">
                    <i className="modern-field-icon fas fa-phone"></i>
                    Phone Number
                  </FormLabel>
                  <FormControlWithFocus fieldName="phoneNumber">
                    <PhoneInputWithCountry
                      value={field.value || ''}
                      onChange={(phoneData) => {
                        // Update phone country, country code, and phone number fields
                        form.setValue('phoneCountry', phoneData.dialCode);
                        form.setValue('phoneCountryCode', phoneData.countryCode);
                        form.setValue('phoneNumber', phoneData.phoneNumber);
                        setTimeout(() => {
                          form.trigger();
                        }, 0);
                      }}
                      placeholder="(555) 123-4567"
                      defaultCountry="US"
                    />
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="phoneNumber"
                    useValidation={true}
                  />

                  <span className='lable_info mt-0 mb-0'>
                    Get real-time alerts to stay on top of messages from subscribers who tip or purchase content.
                  </span>
                </FormItem>
              )}
            />


            {/* ID Upload */}
            <div className="id-upload-section">
              <label className="id-upload-label">
                <i className="id-upload-icon fas fa-id-card"></i>
                ID Verification
              </label>
              <div className="id-upload-info">
                <p className="id-upload-info-text">
                  Upload your ID or passport and a selfie for verification
                </p>
              </div>

              <FormField
                control={form.control}
                name="documentType"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="document-type-selection">
                        <div className="document-type-option">
                          <input
                            id="id-type-passport"
                            name="id-type"
                            type="radio"
                            className="document-radio"
                            checked={field.value === DOCUMENT_TYPES.PASSPORT}
                            onChange={() => field.onChange(DOCUMENT_TYPES.PASSPORT)}
                          />
                          <label htmlFor="id-type-passport" className="document-label">
                            <i className="document-label-icon fas fa-passport"></i>
                            Passport
                          </label>
                        </div>
                        <div className="document-type-option">
                          <input
                            id="id-type-id"
                            name="id-type"
                            type="radio"
                            className="document-radio"
                            checked={field.value === DOCUMENT_TYPES.ID}
                            onChange={() => field.onChange(DOCUMENT_TYPES.ID)}
                          />
                          <label htmlFor="id-type-id" className="document-label">
                            <i className="document-label-icon fas fa-id-card"></i>
                            ID
                          </label>
                        </div>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="space-y-6">
                {/* Document Upload Section */}
                {form.watch('documentType') === DOCUMENT_TYPES.ID ? (
                  <>
                    {/* Front of ID */}
                    <UserUploadFile
                      fileType={FILE_TYPES.ID_FRONT}
                      uploadedFile={uploadedFiles.idFront}
                      onFileUpload={handleFileUpload}
                      onFileRemove={handleRemoveFile}
                      label="Front of ID"
                      icon="fas fa-address-card"
                      uploadText="Upload front"
                      isRequired={true}
                    />

                    {/* Back of ID */}
                    <UserUploadFile
                      fileType={FILE_TYPES.ID_BACK}
                      uploadedFile={uploadedFiles.idBack}
                      onFileUpload={handleFileUpload}
                      onFileRemove={handleRemoveFile}
                      label="Back of ID"
                      icon="fas fa-credit-card"
                      uploadText="Upload back"
                      isRequired={true}
                    />
                  </>
                ) : (
                  /* Passport Upload */
                  <UserUploadFile
                    fileType={FILE_TYPES.ID_FRONT}
                    uploadedFile={uploadedFiles.idFront}
                    onFileUpload={handleFileUpload}
                    onFileRemove={handleRemoveFile}
                    label="Passport (Front Page Only)"
                    icon="fas fa-passport"
                    uploadText="Upload passport"
                    isPassport={true}
                    isRequired={true}
                  />
                )}

                {/* Selfie Photo - Required for both document types */}
                <UserUploadFile
                  fileType={FILE_TYPES.PROFILE_IMAGE}
                  uploadedFile={uploadedFiles.profileImage}
                  onFileUpload={handleFileUpload}
                  onFileRemove={handleRemoveFile}
                  label="Selfie Photo"
                  label2="Submit a clear selfie showing only your face, taken from the shoulders up. Please do not include any IDs, passports, or other documents in the photo."
                  icon="fas fa-person"
                  uploadText="Upload selfie"
                  isRequired={true}
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem id="terms-section" className="terms-section">
                  <FormControlWithFocus fieldName="terms">
                    <div className="terms-wrapper">
                      <div className="terms-checkbox-container">
                        <input
                          id="terms"
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="terms-checkbox"
                        />
                      </div>
                      <div className="terms-label-container">
                        <FormLabel htmlFor="terms" className="terms-label">
                          I agree to the{' '}
                          <span className="terms-link" onClick={handleTermsClick}>
                            Terms of Service
                          </span>{' '}
                          and{' '}
                          <span className="terms-link" onClick={handlePrivacyClick}>
                            Privacy Policy
                          </span>
                        </FormLabel>
                      </div>
                    </div>
                  </FormControlWithFocus>
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <button
              type="submit"
              className="creator-register-submit-btn paddingTOp"
              disabled={!isCreateEnabled || uiState.isLoading}
            >
              <span className="creator-register-submit-text">
                {uiState.isLoading || form.formState.isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    {' '}Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </span>
              <div className="creator-register-submit-shimmer"></div>
            </button>
          </form>
        </Form>

        {/* Login Link */}
        <div className="login-link-box">
          <p className="login-link-text">
            Already have an account?{' '}
            <span
              className="login-link-span modern-login-link"
              onClick={handleLoginLink}
            >
              Log in
            </span>
          </p>
        </div>
      </main>
    </div>
  );
};

export default CreatorRegistration; 